// The dashboard's client error sink — the ONE place a crash the SPA caught (or
// failed to catch) is reported from. Before it, a render throw was contained by
// `<AppErrorBoundary/>` and shown as "something went wrong" with nothing leaving
// the component: no console line, no event, no signal an operator could see.
//
// Three doors feed it: the boundary's fallback (`render`), and the window's
// `error` / `unhandledrejection` (`installClientErrorReporting`, armed once in
// `main.tsx`).
//
// WHERE IT GOES: always the browser console. To `POST /v1/client-errors`
// (`apps/api/src/routes/client-errors.ts`) ONLY for a logged-in session (owner
// ruling 2026-11-01, #7785), which forwards to the platform's crash reporter
// server-side — the reporter holds a DSN write credential, so the browser never
// talks to it directly.
//
// WHAT "LOGGED IN" MEANS HERE is what `<App/>`'s gate reads, read again at crash
// time rather than copied: the `session_me` query holds a `me`, and the 401 latch
// (`session-expiry.ts`) has not fired ({@link useClientErrorSession}). Unbound, the
// sink is closed, so a crash before the probe answers stays in the console too.
//
// The route is STRICT: an unknown key is a 400, each field has a cap, the body
// is capped at 32 KiB, and each session gets 30 reports a minute. So the body is
// built here to fit (`clientErrorBody`) — a real crash must not be refused for its
// size — and the send is fire-and-forget that NEVER throws and never reports its
// own failure (`sendClientError`): a reporter that reports itself is a loop.

import { onCleanup } from 'solid-js';
import { API_BASE_URL } from './api';
import { ApiError } from './api-errors';
import { SESSION_TOKEN, useSessionMe } from './session';
import { sessionExpired } from './session-expiry';

/** Which door the crash came through. */
export type ClientErrorSource = 'render' | 'error' | 'unhandledrejection';

/** One caught crash, as reported. */
export type ClientErrorReport = {
  readonly source: ClientErrorSource;
  readonly error: unknown;
  /** The page it happened on — `location.pathname`, never the query string. */
  readonly path: string;
  /** The API's correlation id when the crash was a failed `/v1` call. */
  readonly requestId?: string | undefined;
};

/** Where a report goes. Injected by tests; the app uses {@link reportClientError}. */
export type ClientErrorReporter = (report: ClientErrorReport) => void;

/** Build the report for `error` thrown through `source` on the current page. */
export function clientErrorReport(source: ClientErrorSource, error: unknown): ClientErrorReport {
  const path = typeof window === 'undefined' ? '' : window.location.pathname;
  return error instanceof ApiError
    ? { source, error, path, requestId: error.requestId }
    : { source, error, path };
}

/** The route's per-field caps (`ClientErrorBodySchema`, apps/api). */
const MESSAGE_MAX = 2_000;
const STACK_MAX = 16_000;
const PATH_MAX = 2_048;
const REQUEST_ID_MAX = 128;
/** The route's whole-body cap, in UTF-8 bytes. */
export const CLIENT_ERROR_BODY_MAX_BYTES = 32 * 1024;

/** The wire body of `POST /v1/client-errors` — exactly its keys, no others. */
export type ClientErrorBody = {
  readonly source: ClientErrorSource;
  readonly message: string;
  readonly stack?: string;
  readonly path: string;
  readonly request_id?: string;
};

/** What was thrown, as a sentence: an Error's message, or what a non-Error WAS. */
function describe(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    const message = error.message === '' ? error.name || 'Error' : error.message;
    return typeof error.stack === 'string' && error.stack !== ''
      ? { message, stack: error.stack }
      : { message };
  }
  let shown: string;
  try {
    shown = typeof error === 'string' ? error : (JSON.stringify(error) ?? String(error));
  } catch {
    shown = '<unserializable>';
  }
  return { message: `${typeof error} thrown: ${shown}` };
}

const utf8Bytes = (body: ClientErrorBody): number =>
  new TextEncoder().encode(JSON.stringify(body)).length;

/**
 * The report as the route accepts it: each field cut to its cap, an empty
 * message or path replaced (the route requires both non-empty), and the stack
 * trimmed until the whole body fits the byte cap — the per-field caps count
 * characters, and 16k multi-byte characters alone overrun 32 KiB.
 */
export function clientErrorBody(report: ClientErrorReport): ClientErrorBody {
  const { message, stack } = describe(report.error);
  const base = {
    source: report.source,
    message: message.slice(0, MESSAGE_MAX),
    path: (report.path === '' ? '/' : report.path).slice(0, PATH_MAX),
    ...(report.requestId ? { request_id: report.requestId.slice(0, REQUEST_ID_MAX) } : {}),
  };
  let trace = stack?.slice(0, STACK_MAX);
  let body: ClientErrorBody = trace ? { ...base, stack: trace } : base;
  while (trace && utf8Bytes(body) > CLIENT_ERROR_BODY_MAX_BYTES) {
    trace = trace.slice(0, Math.floor(trace.length / 2));
    body = trace ? { ...base, stack: trace } : base;
  }
  return body;
}

/** A 429 closes the sink for the route's whole window (`WINDOW_MS`, apps/api). */
const SINK_PAUSE_AFTER_429_MS = 60_000;

/** When the sink may next POST. Page-lifetime state about the sink, not about the session. */
let sinkClosedUntil = 0;

const NO_SESSION = (): boolean => false;
/** Asked at crash time. See the file header. */
let sessionLive: () => boolean = NO_SESSION;

/**
 * Open the sink for as long as `live` answers true. Returns the disposer, which
 * closes it. A new binding is a new session, so whatever the sink refused for the
 * last one is forgotten.
 */
export function bindClientErrorSession(live: () => boolean): () => void {
  sessionLive = live;
  sinkClosedUntil = 0;
  return () => {
    if (sessionLive === live) sessionLive = NO_SESSION;
  };
}

/**
 * Bind the sink to the gate's own `/v1/me` query and 401 latch. Mounted once, inside
 * the query provider (`main.tsx`). It reuses `<App/>`'s query rather than mirroring
 * it into a signal, so there is one answer to "is someone signed in" and no effect
 * that could lag behind it.
 */
export function useClientErrorSession(token: string | null = SESSION_TOKEN): void {
  const me = useSessionMe(token);
  onCleanup(bindClientErrorSession(() => me.data !== undefined && !sessionExpired()));
}

/**
 * Fire the report. Resolves, always: a refused or failed POST (network down,
 * 400) is dropped on the floor, never thrown, retried or reported — this is
 * the one call whose failure must not reach the sink. Plain `fetch`, not
 * `apiFetch`: a 401 here must not trip the session latch, and a crash report is
 * not a `/v1` read the mocks should answer. `keepalive` lets it outlive a page
 * that is unloading on the crash.
 *
 * Two answers close the sink, each naming a loop:
 *  - 401: the cookie died and no `/v1` call has noticed yet, or the page is unloading
 *    after sign-out. Closed for the page: every further crash would be a refused POST
 *    charged to the per-IP bucket this browser's sign-in shares.
 *  - 429: every POST charges the session's write budget before the route's own
 *    bucket answers, so a crash loop posting on would starve the person's real writes.
 */
export async function sendClientError(
  body: ClientErrorBody,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  try {
    const res = await fetchImpl(`${API_BASE_URL}/v1/client-errors`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 401) sinkClosedUntil = Number.POSITIVE_INFINITY;
    if (res.status === 429) sinkClosedUntil = Date.now() + SINK_PAUSE_AFTER_429_MS;
  } catch {
    // Deliberately silent — see the docblock. The crash itself was logged.
  }
}

/** A reporter over a transport: one `console.error` line, then one send. */
export function createClientErrorReporter(
  send: (body: ClientErrorBody) => unknown,
): ClientErrorReporter {
  return (report) => {
    console.error(
      `[dashboard] ${report.source} crash on ${report.path}`,
      report.requestId === undefined ? '' : `request ${report.requestId}`,
      report.error,
    );
    try {
      void send(clientErrorBody(report));
    } catch {
      // Building or sending the report must never become a second crash.
    }
  };
}

/** The app's reporter: the console line, plus `POST /v1/client-errors` while the sink is open. */
export const reportClientError: ClientErrorReporter = createClientErrorReporter((body) =>
  sessionLive() && Date.now() >= sinkClosedUntil ? sendClientError(body) : undefined,
);

/**
 * The browser's ResizeObserver loop notice (Chrome's current and older wording).
 * Chrome fires it as a window `error` with NO Error object when an observer's
 * callback resizes what it observes; the notifications are delivered next frame
 * and nothing threw. It reached the crash reporter as `string thrown: …`
 * (GlitchTip project 5, #124/#129), so it is dropped at the `error` door — only
 * when it arrives message-only, so a real Error saying the same words still reports.
 */
const BENIGN_ERROR_MESSAGES: ReadonlySet<string> = new Set([
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
]);

/**
 * Arm the window-level doors: an uncaught throw (`error`) and a rejected promise
 * nobody handled (`unhandledrejection`). Armed for every visitor, because the
 * console line is; whether a report also leaves the browser is the sink's call.
 * Returns the disposer.
 */
export function installClientErrorReporting(
  target: Window,
  report: ClientErrorReporter = reportClientError,
): () => void {
  const onError = (e: ErrorEvent): void => {
    if (e.error == null && BENIGN_ERROR_MESSAGES.has(e.message)) return;
    report(clientErrorReport('error', e.error ?? e.message));
  };
  const onRejection = (e: PromiseRejectionEvent): void =>
    report(clientErrorReport('unhandledrejection', e.reason));
  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
}
